import { isRefreshTokenRejected, refreshMemberToken, type MemberLoginResult } from "@/api/member";

let distributorRefreshPromise: Promise<string | null> | null = null;

export interface DistributorSession {
  accessToken: string;
  accessTokenExpires: number;
  refreshToken: string;
  refreshTokenExpires: number;
  email: string;
  name: string;
  avatar: string;
}

const STORAGE_KEY = "storybox_distributor_session";

export function saveDistributorSession(result: MemberLoginResult): DistributorSession {
  const session: DistributorSession = {
    accessToken: result.accessToken,
    accessTokenExpires: result.accessTokenExpires,
    refreshToken: result.refreshToken,
    refreshTokenExpires: result.refreshTokenExpires,
    email: result.user.email || "",
    name: result.user.nickName || result.user.userName || result.user.email || "Distributor",
    avatar: result.user.avatar || "",
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  return session;
}

export function readDistributorSession(): DistributorSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as DistributorSession) : null;
  } catch {
    return null;
  }
}

export function clearDistributorSession() {
  localStorage.removeItem(STORAGE_KEY);
}

export async function ensureDistributorAccessToken(): Promise<string | null> {
  const session = readDistributorSession();
  if (!session?.accessToken) {
    return null;
  }
  const now = Math.floor(Date.now() / 1000);
  if (session.accessTokenExpires && session.accessTokenExpires - now > 60) {
    return session.accessToken;
  }
  if (!session.refreshToken || (session.refreshTokenExpires && session.refreshTokenExpires <= now)) {
    clearDistributorSession();
    return null;
  }
  if (!distributorRefreshPromise) {
    distributorRefreshPromise = (async () => {
      try {
        const refreshed = await refreshMemberToken(session.refreshToken);
        const current = readDistributorSession();
        if (current?.refreshToken !== session.refreshToken) {
          return current?.accessToken || null;
        }
        const next = {
          ...session,
          accessToken: refreshed.accessToken,
          accessTokenExpires: refreshed.accessTokenExpires,
          refreshToken: refreshed.refreshToken,
          refreshTokenExpires: refreshed.refreshTokenExpires,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        return next.accessToken;
      } catch (error) {
        if (isRefreshTokenRejected(error)) {
          clearDistributorSession();
          return null;
        }
        throw error;
      }
    })().finally(() => {
      distributorRefreshPromise = null;
    });
  }
  return distributorRefreshPromise;
}
